import { useState, useEffect, useCallback, useRef } from "react";
import networkService from "../services/NetworkService";

export const useNetworkService = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const eventListenersRef = useRef(new Set());

  // Connection status handler
  const handleConnectionStatus = useCallback((data) => {
    setIsConnected(data.connected);
    if (!data.connected) {
      setConnectionError(data.reason || "Disconnected");
    } else {
      setConnectionError(null);
      // Sync player ID when connection is established
      const currentPlayerId = networkService.getPlayerId();
      setPlayerId(currentPlayerId);
    }
  }, []);

  // Connection error handler
  const handleConnectionError = useCallback((data) => {
    setConnectionError(data.error.message || "Connection failed");
  }, []);

  // Player joined handler
  const handlePlayerJoined = useCallback((data) => {
    setRoomData((prevRoom) => {
      if (!prevRoom) return null;
      return {
        ...prevRoom,
        players: [...prevRoom.players, data.player],
      };
    });
  }, []);

  // Player left handler
  const handlePlayerLeft = useCallback((data) => {
    setRoomData((prevRoom) => {
      if (!prevRoom) return null;
      return {
        ...prevRoom,
        players: data.players,
      };
    });
  }, []);

  // Player ready status changed handler
  const handlePlayerReadyChanged = useCallback((data) => {
    setRoomData((prevRoom) => {
      if (!prevRoom) return null;
      return {
        ...prevRoom,
        players: data.players,
      };
    });
  }, []);

  // Game started handler
  const handleGameStarted = useCallback((data) => {
    // This will be handled by the game components
  }, []);

  // Room state updated handler (for return to lobby)
  const handleRoomStateUpdated = useCallback(
    (data) => {
      if (data.room) {
        setRoomData(data.room);
      }
    },
    [] // Remove roomData dependency to prevent callback recreation
  );

  // Game settings updated handler
  const handleGameSettingsUpdated = useCallback(
    (data) => {
      setRoomData((prevRoom) => {
        if (!prevRoom) {
          return null;
        }
        const updatedRoom = {
          ...prevRoom,
          gameSettings: data.gameSettings,
        };
        return updatedRoom;
      });
    },
    [roomData]
  );

  // Player disconnected during game handler
  const handlePlayerDisconnectedInGame = useCallback((data) => {
    // Update room data to remove the disconnected player
    setRoomData((prevRoom) => {
      if (!prevRoom) return null;
      return {
        ...prevRoom,
        players: data.players,
      };
    });

    // Show notification (this will be handled by components listening to this event)
  }, []);

  // Setup event listeners
  useEffect(() => {
    const listeners = [
      { event: "connection_status", handler: handleConnectionStatus },
      { event: "connection_error", handler: handleConnectionError },
      { event: "player_joined", handler: handlePlayerJoined },
      { event: "player_left", handler: handlePlayerLeft },
      { event: "player_ready_changed", handler: handlePlayerReadyChanged },
      { event: "game_started", handler: handleGameStarted },
      { event: "room_state_updated", handler: handleRoomStateUpdated },
      { event: "game_settings_updated", handler: handleGameSettingsUpdated },
      {
        event: "player_disconnected_ingame",
        handler: handlePlayerDisconnectedInGame,
      },
    ];

    listeners.forEach(({ event, handler }) => {
      networkService.on(event, handler);
      eventListenersRef.current.add({ event, handler });
    });

    // Sync initial state from service
    const currentPlayerId = networkService.getPlayerId();
    const currentConnectionStatus = networkService.getConnectionStatus();

    if (currentPlayerId && !playerId) {
      setPlayerId(currentPlayerId);
    }

    if (currentConnectionStatus !== isConnected) {
      setIsConnected(currentConnectionStatus);
    }

    return () => {
      eventListenersRef.current.forEach(({ event, handler }) => {
        networkService.off(event, handler);
      });
      eventListenersRef.current.clear();
    };
  }, [
    handleConnectionStatus,
    handleConnectionError,
    handlePlayerJoined,
    handlePlayerLeft,
    handlePlayerReadyChanged,
    handleGameStarted,
    handleRoomStateUpdated,
    handleGameSettingsUpdated,
    handlePlayerDisconnectedInGame,
    playerId,
    isConnected,
  ]);

  // Connect to server
  const connect = useCallback(async (serverUrl) => {
    setIsLoading(true);
    setConnectionError(null);

    try {
      const success = networkService.connect(serverUrl);
      if (!success) {
        throw new Error("Failed to initialize connection");
      }

      // Wait for connection to establish with a longer timeout
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts && !networkService.getConnectionStatus()) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        attempts++;
      }

      if (networkService.getConnectionStatus()) {
        setPlayerId(networkService.getPlayerId());
        return true;
      } else {
        throw new Error("Connection timeout");
      }
    } catch (error) {
      setConnectionError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create room
  const createRoom = useCallback(
    async (playerName, theme = "theme-1", boardSkin = null) => {
      if (!isConnected) {
        throw new Error("Not connected to server");
      }

      setIsLoading(true);
      try {
        const response = await networkService.createRoom(playerName, theme, boardSkin);
        setRoomData(response.room);
        setPlayerId(response.playerId);
        return response;
      } catch (error) {
        setConnectionError(error.message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isConnected]
  );

  // Join room
  const joinRoom = useCallback(
    async (roomCode, playerName, theme = "theme-1", boardSkin = null) => {
      if (!isConnected) {
        throw new Error("Not connected to server");
      }

      setIsLoading(true);
      try {
        const response = await networkService.joinRoom(roomCode, playerName, theme, boardSkin);
        setRoomData(response.room);
        setPlayerId(response.playerId);
        return response;
      } catch (error) {
        setConnectionError(error.message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isConnected]
  );

  // Toggle ready status
  const toggleReady = useCallback(
    async (isReady) => {
      if (!isConnected || !playerId) {
        throw new Error("Not connected or no player ID");
      }

      setIsLoading(true);
      try {
        await networkService.toggleReady(isReady);
      } catch (error) {
        setConnectionError(error.message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isConnected, playerId]
  );

  // Start game
  const startGame = useCallback(async () => {
    if (!isConnected || !playerId) {
      throw new Error("Not connected or no player ID");
    }

    setIsLoading(true);
    try {
      await networkService.startGame();
    } catch (error) {
      setConnectionError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, playerId]);

  // Leave room
  const leaveRoom = useCallback(() => {
    networkService.leaveRoom();
    setRoomData(null);
    setPlayerId(null);
    setIsConnected(false);
  }, []);

  // Disconnect
  const disconnect = useCallback(() => {
    networkService.disconnect();
    setRoomData(null);
    setPlayerId(null);
    setIsConnected(false);
    setConnectionError(null);
  }, []);

  // Return to lobby (sync room state)
  const returnToLobby = useCallback(
    async () => {
      if (!isConnected || !playerId) {
        throw new Error("Not connected or no player ID");
      }

      setIsLoading(true);
      try {
        const response = await networkService.returnToLobby();
        return response;
      } catch (error) {
        setConnectionError(error.message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isConnected, playerId]
  );

  // Send game event
  const sendGameEvent = useCallback(
    async (eventType, eventData) => {
      if (!isConnected || !playerId) {
        throw new Error("Not connected or no player ID");
      }

      try {
        await networkService.sendGameEvent(eventType, eventData);
      } catch (error) {
        setConnectionError(error.message);
        throw error;
      }
    },
    [isConnected, playerId]
  );

  // Update game settings (host only)
  const updateGameSettings = useCallback(
    async (gameSettings) => {
      if (!isConnected || !playerId) {
        throw new Error("Not connected or no player ID");
      }

      setIsLoading(true);
      try {
        const response = await networkService.updateGameSettings(
          playerId,
          gameSettings
        );
        return response;
      } catch (error) {
        setConnectionError(error.message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isConnected, playerId]
  );

  return {
    // State
    isConnected,
    connectionError,
    roomData,
    playerId,
    isLoading,

    // Actions
    connect,
    createRoom,
    joinRoom,
    toggleReady,
    startGame,
    updateGameSettings,
    returnToLobby,
    sendGameEvent,
    leaveRoom,
    disconnect,

    // Utilities
    clearError: () => setConnectionError(null),
  };
};
